import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Eye, Lock } from 'lucide-react';
import ReactDOM from 'react-dom';
import { Modal } from 'react-bootstrap';
import { fetchManagerWorkers, updateManagerWorkerPassword, updateManagerWorkerStatus } from '../../services/api';
import { AddNewUserModal } from './AddNewUserModal';
import { toast } from 'react-toastify';
import Preloader from '../../components/Preloder';
import { logger } from '../../utils/logger';

type WorkerStatus = 'active' | 'inactive';

interface Worker {
    id: string;
    name: string;
    email: string;
    role: string;
    status: WorkerStatus;
    created_at: string;
    last_login_at?: string | null;
}

// Component functions
function toTitle(v: string) { return v.replace(/_/g, ' ').replace(/^\w|\s\w/g, (m) => m.toUpperCase()); }

function StatusToggle({ active, onToggle, isLoading }: { active: boolean; onToggle: () => void; isLoading?: boolean }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            disabled={isLoading}
            className={`global-swicth-toggle relative w-12 h-6 rounded-full transition-colors ${active ? 'bg-green-500' : 'bg-red-500'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-pressed={active}
        >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${active ? 'translate-x-5' : 'translate-x-0.5'}`} />
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
            )}
        </button>
    );
}

// Pagination helper functions (same as Reports page)
type PageButton = { key: string; page?: number; label: string | number; active?: boolean; disabled?: boolean };
function getPageNumbers(current: number, total: number): PageButton[] {
    const buttons: PageButton[] = [];
    const pushPage = (p: number, active = false) => buttons.push({ key: `p-${p}`, page: p, label: p, active });
    const pushEllipsis = (id: string) => buttons.push({ key: `e-${id}`, label: '…', disabled: true });
    if (total <= 5) {
        for (let i = 1; i <= total; i++) pushPage(i, i === current);
        return buttons;
    }
    pushPage(1, current === 1);
    if (current > 3) pushEllipsis('l');
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pushPage(i, i === current);
    if (current < total - 2) pushEllipsis('r');
    pushPage(total, current === total);
    return buttons;
}

export const MyWorkers: React.FC = () => {
    const [search, setSearch] = useState('');
    const [statusTab, setStatusTab] = useState<WorkerStatus>('active');
    const [workers, setWorkers] = useState<Worker[]>([]);
    const [displayWorkers, setDisplayWorkers] = useState<Worker[]>([]);
    const [menuFor, setMenuFor] = useState<string | null>(null);
    const [addUserModalOpen, setAddUserModalOpen] = useState(false);
    const [viewUserModalOpen, setViewUserModalOpen] = useState(false);
    const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
    const [selectedWorker, setSelectedWorker] = useState<Worker | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(9);
    const isUpdatingStatusRef = useRef(false);

    const loadWorkers = useCallback(async () => {
        try {
            setIsLoading(true);
            const res = await fetchManagerWorkers({ page: 1, pageSize: 100, status: statusTab });
            setWorkers(res.items);
        } catch (error) {
            // Silently handle fetch errors
        } finally {
            setIsLoading(false);
            setInitialLoading(false); // Set initial loading to false after first load
        }
    }, [statusTab]);

    // Load workers from API on component mount and when status tab changes
    useEffect(() => {
        loadWorkers();
    }, [loadWorkers]);

    // Filter workers whenever workers data changes (for local filtering if needed)
    useEffect(() => {
        // Don't update displayWorkers if we're in the middle of a status update
        if (isUpdatingStatusRef.current) {
            return;
        }

        // Since search and status filtering are now handled by the API,
        // we just set displayWorkers to match workers
        setDisplayWorkers(workers);
        setPage(1); // Reset to first page when data changes
    }, [workers]);

    // Calculate pagination
    const totalPages = Math.max(1, Math.ceil(displayWorkers.length / pageSize));
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const pageItems = displayWorkers.slice(startIndex, endIndex);

    const handleSearch = async (query: string) => {
        setSearch(query);
        setPage(1); // Reset to first page when searching
        // Reload workers with new search term
        try {
            setIsLoading(true);
            const res = await fetchManagerWorkers({ page: 1, pageSize: 100, status: statusTab, search: query });
            setWorkers(res.items);
        } catch (error) {
            // Silently handle fetch errors
        } finally {
            setIsLoading(false);
        }
    };

    const toggleWorkerStatus = async (id: string) => {
        try {
            setIsUpdatingStatus(id);
            isUpdatingStatusRef.current = true;

            const worker = workers.find(w => w.id === id);
            if (!worker) return;

            const newStatus = worker.status === 'active' ? 'inactive' : 'active';

            // Call the API to update the worker status
            await updateManagerWorkerStatus(id, newStatus);

            // Refresh the table data from server
            const res = await fetchManagerWorkers({ page: 1, pageSize: 100 });
            const updatedWorkers = res.items;
            
            // Update workers state
            setWorkers(updatedWorkers);
            
            // Manually update displayWorkers to bypass the useEffect guard
            const filtered = updatedWorkers.filter((worker: Worker) => {
                const matchesText = search === '' || `${worker.name} ${worker.email}`.toLowerCase().includes(search.toLowerCase());
                const matchesStatus = worker.status === statusTab;
                return matchesText && matchesStatus;
            });
            setDisplayWorkers(filtered);

            toast.success(`Worker status updated to ${newStatus}`);
        } catch (error: any) {
            const errorMessage = error?.message || "Failed to update worker status";
            toast.error(errorMessage);
            logger.error('Error updating worker status:', error);
        } finally {
            // Small delay to ensure the ref is properly reset
            setTimeout(() => {
                setIsUpdatingStatus(null);
                isUpdatingStatusRef.current = false;
            }, 100);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    // Show preloader while data is loading
    if (initialLoading) {
        return <Preloader />;
    }

    return (
        <div className="space-y-6 min-h-0">
            {/* Controls */}
            <div className="flex flex-col lg:flex-row gap-3 lg:gap-4 items-stretch lg:items-center">
                <div className="w-80">
                    <div className="relative">
                        <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                            <img src="/icons/searchIcon.png" alt="Search" className="w-4 h-4" />
                        </span>
                        <input
                            value={search}
                            onChange={(e) => handleSearch(e.target.value)}
                            placeholder="Search workers.."
                            className="w-full h-10 pl-9 pr-4 py-1 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                        />
                    </div>
                </div>
                <div className="flex items-center">
                    <div className="flex items-center rounded-xl border border-gray-300 overflow-hidden bg-white p-1 w-48 h-10">
                        <button
                            className={`flex-1 h-8 text-sm rounded-md transition-all ${statusTab === 'active' ? 'bg-green-600 text-white shadow-sm font-semibold' : 'text-gray-500'}`}
                            onClick={() => setStatusTab('active')}
                        >
                            Active
                        </button>
                        <button
                            className={`flex-1 h-8 text-sm rounded-md transition-all ${statusTab === 'inactive' ? 'bg-red-500 text-white shadow-sm font-semibold' : 'text-gray-500'}`}
                            onClick={() => setStatusTab('inactive')}
                        >
                            Inactive
                        </button>
                    </div>
                </div>
                <div className="flex-none ml-auto flex gap-3">
                    <button
                        onClick={() => setAddUserModalOpen(true)}
                        className="renegade-btn-primary mini-btn"
                    >
                        <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M0.5 13C0.5 6.09625 6.09625 0.5 13 0.5C19.9037 0.5 25.5 6.09625 25.5 13C25.5 19.9037 19.9037 25.5 13 25.5C6.09625 25.5 0.5 19.9037 0.5 13ZM13 3C10.3478 3 7.8043 4.05357 5.92893 5.92893C4.05357 7.8043 3 10.3478 3 13C3 15.6522 4.05357 18.1957 5.92893 20.0711C7.8043 21.9464 10.3478 23 13 23C15.6522 23 18.1957 21.9464 20.0711 20.0711C21.9464 18.1957 23 15.6522 23 13C23 10.3478 21.9464 7.8043 20.0711 5.92893C18.1957 4.05357 15.6522 3 13 3Z" fill="white" />
                            <path fillRule="evenodd" clipRule="evenodd" d="M14.25 6.75C14.25 6.41848 14.1183 6.10054 13.8839 5.86612C13.6495 5.6317 13.3315 5.5 13 5.5C12.6685 5.5 12.3505 5.6317 12.1161 5.86612C11.8817 6.10054 11.75 6.41848 11.75 6.75V11.75H6.75C6.41848 11.75 6.10054 11.8817 5.86612 12.1161C5.6317 12.3505 5.5 12.6685 5.5 13C5.5 13.3315 5.6317 13.6495 5.86612 13.8839C6.10054 14.1183 6.41848 14.25 6.75 14.25H11.75V19.25C11.75 19.5815 11.8817 19.8995 12.1161 20.1339C12.3505 20.3683 12.6685 20.5 13 20.5C13.3315 20.5 13.6495 20.3683 13.8839 20.1339C14.1183 19.8995 14.25 19.5815 14.25 19.25V14.25H19.25C19.5815 14.25 19.8995 14.1183 20.1339 13.8839C20.3683 13.6495 20.5 13.3315 20.5 13C20.5 12.6685 20.3683 12.3505 20.1339 12.1161C19.8995 11.8817 19.5815 11.75 19.25 11.75H14.25V6.75Z" fill="white" />
                        </svg>
                        Add New User
                    </button>
                </div>
            </div>

            {/* Workers table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Name</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Email</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Role</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Created On</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Status</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-4 text-14-bold grey-800">Loading...</td>
                                </tr>
                            ) : pageItems.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">No workers found</td>
                                </tr>
                            ) : (
                                pageItems.map((worker) => (
                                    <tr key={worker.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 text-14-bold grey-800">{worker.name}</td>
                                        <td className="px-4 py-4 text-14-reg grey-800">{worker.email}</td>
                                        <td className="px-4 py-4 text-14-reg grey-800">{toTitle(worker.role)}</td>
                                        <td className="px-4 py-4 text-14-reg grey-800">{formatDate(worker.created_at)}</td>
                                        <td className="px-4 py-4">
                                            <StatusToggle
                                                active={worker.status === 'active'}
                                                onToggle={() => toggleWorkerStatus(worker.id)}
                                                isLoading={isUpdatingStatus === worker.id}
                                            />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <ActionsMenu
                                                worker={worker}
                                                isOpen={menuFor === worker.id}
                                                onToggle={() => setMenuFor(v => v === worker.id ? null : worker.id)}
                                                onClose={() => setMenuFor(null)}
                                                onViewWorker={(worker) => {
                                                    setSelectedWorker(worker);
                                                    setViewUserModalOpen(true);
                                                }}
                                                onResetPassword={(worker) => {
                                                    setSelectedWorker(worker);
                                                    setResetPasswordModalOpen(true);
                                                }}
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="global-pagination px-4 md:px-6 py-3 flex items-center justify-between border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm">
                        <button
                            className="btn-next-prev"
                            disabled={page === 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            <i className="iconoir-nav-arrow-left"></i>
                        </button>
                        <div className="flex items-center gap-1">
                            {getPageNumbers(page, totalPages).map((p: PageButton) => (
                                <button
                                    key={p.key}
                                    disabled={p.disabled}
                                    onClick={() => typeof p.page === 'number' && setPage(p.page)}
                                    className={`btn-page-number ${p.active ? 'color-renegade' : ''}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <button
                            className="btn-next-prev"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                            <i className="iconoir-nav-arrow-right"></i>
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min="1"
                            max="100"
                            value={pageSize}
                            onChange={(e) => {
                                const newPageSize = parseInt(e.target.value) || 10;
                                if (newPageSize >= 1 && newPageSize <= 100) {
                                    setPageSize(newPageSize);
                                    setPage(1); // Reset to first page when page size changes
                                }
                            }}
                            className="page-size-input"
                        />
                        <span className="text-14-bold grey-400">Rows per page</span>
                    </div>
                </div>
            </div>

            {/* Add New User Modal */}
            <AddNewUserModal
                show={addUserModalOpen}
                onClose={() => setAddUserModalOpen(false)}
                onCreated={(newWorker) => {
                    setWorkers(prev => [newWorker, ...prev]);
                }}
            />

            {/* Worker Info Modal */}
            {viewUserModalOpen && selectedWorker && (
                <WorkerInfoModal
                    worker={selectedWorker}
                    onClose={() => {
                        setViewUserModalOpen(false);
                        setSelectedWorker(null);
                    }}
                />
            )}

            {/* Reset Password Modal */}
            {resetPasswordModalOpen && selectedWorker && (
                <ResetPasswordModal
                    worker={selectedWorker}
                    onClose={() => {
                        setResetPasswordModalOpen(false);
                        setSelectedWorker(null);
                    }}
                />
            )}
        </div>
    );
};

function ActionsMenu({ worker, isOpen, onToggle, onClose, onViewWorker, onResetPassword }: { worker: Worker; isOpen: boolean; onToggle: () => void; onClose: () => void; onViewWorker: (worker: Worker) => void; onResetPassword: (worker: Worker) => void }) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

    function computePosition() {
        const btn = buttonRef.current;
        if (!btn) return;
        const rect = btn.getBoundingClientRect();

        // Use measured size if available, otherwise sensible defaults
        const menuWidth = (menuRef.current?.offsetWidth) ?? 224;
        const menuHeight = (menuRef.current?.offsetHeight) ?? 200;

        const margin = 8;
        // Prefer bottom-right aligned to trigger right edge
        let top = rect.bottom + margin;
        let left = Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - margin);

        // Flip up if not enough room below
        if (top + menuHeight > window.innerHeight) {
            top = rect.top - menuHeight - margin;
        }
        // Clamp within viewport
        if (top < margin) top = margin;
        if (left < margin) left = margin;

        setCoords({ top, left });
    }

    useLayoutEffect(() => {
        if (!isOpen) return;
        computePosition();
        const handle = () => computePosition();
        window.addEventListener('scroll', handle, true);
        window.addEventListener('resize', handle);
        return () => {
            window.removeEventListener('scroll', handle, true);
            window.removeEventListener('resize', handle);
        };
    }, [isOpen]);

    useEffect(() => {
        function onDocClick(e: MouseEvent) {
            const target = e.target as Node;
            if (
                menuRef.current && !menuRef.current.contains(target) &&
                buttonRef.current && !buttonRef.current.contains(target)
            ) onClose();
        }
        function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
        if (isOpen) {
            document.addEventListener('mousedown', onDocClick);
            document.addEventListener('keydown', onKey);
        }
        return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onKey); };
    }, [isOpen, onClose]);

    // Render the menu with visibility hidden first to allow measurement, then show
    const shouldRender = isOpen;
    const isVisible = !!coords;
    const menu = shouldRender ? (
        <div
            ref={menuRef}
            style={{ position: 'fixed', top: (coords?.top ?? -9999), left: (coords?.left ?? -9999), zIndex: 1000, visibility: isVisible ? 'visible' : 'hidden' }}
            className="w-56 rounded-2xl bg-white shadow-lg ring-1 ring-black/5 border border-gray-200"
        >
            <div className="py-2">
                <button
                    className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50"
                    onClick={() => {
                        onViewWorker(worker);
                        onClose();
                    }}
                >
                    <span className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                        <Eye size={16} />
                    </span>
                    <span className="text-sm text-gray-800">View</span>
                </button>
                <button
                    className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50"
                    onClick={() => {
                        onResetPassword(worker);
                        onClose();
                    }}
                >
                    <span className="w-6 h-6 rounded-lg bg-yellow-50 text-yellow-600 flex items-center justify-center">
                        <Lock size={16} />
                    </span>
                    <span className="text-sm text-gray-800">Reset Password</span>
                </button>
            </div>
        </div>
    ) : null;

    return (
        <div ref={containerRef} className="relative inline-block text-left">
            <button ref={buttonRef} type="button" onClick={() => { onToggle(); setTimeout(computePosition, 0); }} className="px-2 py-1 rounded-lg hover:bg-gray-100">
                <svg width="26" height="6" viewBox="0 0 26 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6C2.20435 6 1.44129 5.68393 0.87868 5.12132C0.316071 4.55871 0 3.79565 0 3C0 2.20435 0.316071 1.44129 0.87868 0.878679C1.44129 0.31607 2.20435 0 3 0C3.79565 0 4.55871 0.31607 5.12132 0.878679C5.68393 1.44129 6 2.20435 6 3C6 3.79565 5.68393 4.55871 5.12132 5.12132C4.55871 5.68393 3.79565 6 3 6ZM13 6C12.2044 6 11.4413 5.68393 10.8787 5.12132C10.3161 4.55871 10 3.79565 10 3C10 2.20435 10.3161 1.44129 10.8787 0.878679C11.4413 0.31607 12.2044 0 13 0C13.7956 0 14.5587 0.31607 15.1213 0.878679C15.6839 1.44129 16 2.20435 16 3C16 3.79565 15.6839 4.55871 15.1213 5.12132C14.5587 5.68393 13.7956 6 13 6ZM23 6C22.2044 6 21.4413 5.68393 20.8787 5.12132C20.3161 4.55871 20 3.79565 20 3C20 2.20435 20.3161 1.44129 20.8787 0.878679C21.4413 0.31607 22.2044 0 23 0C23.7956 0 24.5587 0.31607 25.1213 0.878679C25.6839 1.44129 26 2.20435 26 3C26 3.79565 25.6839 4.55871 25.1213 5.12132C24.5587 5.68393 23.7956 6 23 6Z" fill="#616161" />
                </svg>
            </button>
            {menu && ReactDOM.createPortal(menu, document.body)}
        </div>
    );
}



// Reset Password Modal Component
function ResetPasswordModal({ worker, onClose }: { worker: Worker; onClose: () => void }) {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const validatePasswordForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!newPassword) {
            newErrors.newPassword = 'New password is required.';
        } else if (newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters long.';
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#\-_+=])[A-Za-z\d@$!%*?&#\-_+=]{8,}$/.test(newPassword)) {
            newErrors.newPassword = 'Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.';
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Confirm password is required.';
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePasswordField = (fieldName: string, value: string) => {
        const newErrors = { ...errors };

        if (fieldName === 'newPassword') {
            if (!value) {
                newErrors.newPassword = 'New password is required.';
            } else if (value.length < 8) {
                newErrors.newPassword = 'Password must be at least 8 characters long.';
            } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#\-_+=])[A-Za-z\d@$!%*?&#\-_+=]{8,}$/.test(value)) {
                newErrors.newPassword = 'Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.';
            } else {
                delete newErrors.newPassword;
            }

            // Also validate confirm password when new password changes
            if (confirmPassword && confirmPassword !== value) {
                newErrors.confirmPassword = 'Passwords do not match';
            } else if (confirmPassword && confirmPassword === value) {
                delete newErrors.confirmPassword;
            }
        } else if (fieldName === 'confirmPassword') {
            if (!value) {
                newErrors.confirmPassword = 'Confirm password is required.';
            } else if (newPassword !== value) {
                newErrors.confirmPassword = 'Passwords do not match';
            } else {
                delete newErrors.confirmPassword;
            }
        }

        setErrors(newErrors);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrors({});

        // Validation using the validation function
        if (!validatePasswordForm()) {
            return;
        }

        setIsSaving(true);

        try {
            await updateManagerWorkerPassword(worker.id, newPassword, confirmPassword);
            toast.success("Password updated successfully!");
            onClose();
        } catch (error: any) {
            const errorMessage = error?.message || "Failed to update password. Please try again.";
            setErrors({ general: errorMessage });
            toast.error(errorMessage);
            logger.error('Error updating worker password:', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={true} onHide={onClose} centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="text-gray-800">Reset Password</Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={onClose}></i>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                <form onSubmit={handleSubmit}>
                    {/* Enter New Password */}
                    <div className="form-group password-feild">
                        <label htmlFor="newPassword">Enter New Password</label>
                        <input
                            id="newPassword"
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => {
                                setNewPassword(e.target.value);
                                validatePasswordField('newPassword', e.target.value);
                            }}
                            placeholder="Enter new password"
                            autoComplete="new-password"
                            className={`form-control ${errors.newPassword ? 'border-danger' : ''}`}
                        />
                        <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className='password-toggle'
                        >
                            {showNewPassword ? (
                                <i className="iconoir-eye"></i>
                            ) : (
                                <i className="iconoir-eye-closed"></i>
                            )}
                        </button>
                        {errors.newPassword && (
                            <p className="error-message">{errors.newPassword}</p>
                        )}
                    </div>

                    {/* Enter Confirm Password */}
                    <div className="form-group password-feild">
                        <label htmlFor="confirmPassword">Enter Confirm Password</label>
                        <input
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                validatePasswordField('confirmPassword', e.target.value);
                            }}
                            placeholder="Enter confirm password"
                            autoComplete="new-password"
                            className={`form-control ${errors.confirmPassword ? 'border-danger' : ''}`}
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className='password-toggle'
                        >
                            {showConfirmPassword ? (
                                <i className="iconoir-eye"></i>
                            ) : (
                                <i className="iconoir-eye-closed"></i>
                            )}
                        </button>
                        {errors.confirmPassword && (
                            <p className="error-message">{errors.confirmPassword}</p>
                        )}
                    </div>
                    <div className="d-flex justify-content-end gap-3 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="renegade-btn-secondary mini-btn"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="renegade-btn-primary mini-btn"
                        >
                            {isSaving ? 'Updating...' : 'Update Password'}
                        </button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
}


// Worker Info Modal Component
function WorkerInfoModal({ worker, onClose }: { worker: Worker; onClose: () => void }) {
    return (
        <Modal show={true} onHide={onClose} className="std-modal-width" centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="text-gray-800">View User Details</Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={onClose}></i>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                {/* Content */}
                <div>
                    {/* Worker Name */}
                    <div className="mb-3">
                        <label className="text-14-bold grey-800 mb-0">User Name</label>
                        <p className="text-14-reg grey-600">{worker.name}</p>
                    </div>

                    {/* Worker Email */}
                    <div className="mb-3">
                        <label className="text-14-bold grey-800 mb-0">User Email ID</label>
                        <p className="text-14-reg grey-600">{worker.email}</p>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

export default MyWorkers;
